import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'
import { ReviewerAndEmployee, ReviewerOfEmployeeType, EmployeeWithBuFragment } from '../../../types'
import { PerInfoBox } from '../../../components/custom_components/InfoBox'
import { PerInfoBoxDivider } from '../../../components/custom_components/InfoBox/InfoBoxDivider'
import { ReviewerAddModal } from './ReviewerModal'
import { sortEmployeesWithBusByName } from './util'
import { PerInfoBoxTagElement } from '../../../components/custom_components/InfoBox/InfoBoxTagElement'
import { PerInfoBoxNumberElement } from '../../../components/custom_components/InfoBox/InfoBoxNumberElement'
import { PerInfoBoxActionButtonElement } from '../../../components/custom_components/InfoBox/InfoBoxActionButtonElement'
import { useCompanySettingsStore } from '../CompanySettingsStore'
import { InfoBoxTile } from '../../../components/custom_components/InfoBox/InfoBoxTileTemplate/InfoBoxTile'

interface Props {
  activeEmployeesOfCompany: EmployeeWithBuFragment[]
  activeEmployeesOfCompanyWithoutPartners: EmployeeWithBuFragment[]
  existingRelations: ReviewerAndEmployee[]
  canEdit: boolean
}

export const ReviewerInfoBox: FC<Props> = ({
  activeEmployeesOfCompany,
  activeEmployeesOfCompanyWithoutPartners,
  existingRelations,
  canEdit,
}) => {
  const { t } = useTranslation()
  const [selected] = useCompanySettingsStore((state) => [state.selected], shallow)

  const [open, setOpen] = useState(false)

  const [possibleReviewers, setPossibleReviewers] = useState<EmployeeWithBuFragment[]>([])

  const activeEmployees = sortEmployeesWithBusByName(activeEmployeesOfCompany)
  const activeEmployeesWithoutPartners = sortEmployeesWithBusByName(activeEmployeesOfCompanyWithoutPartners)

  const totalOfReviewer = new Set(existingRelations.map((er) => er.reviewer.id)).size
  const totalOfInternal = new Set(existingRelations.filter((er) => er.type === ReviewerOfEmployeeType.Internal).map((er) => er.reviewer.id))
    .size
  const totalOfExternal = new Set(existingRelations.filter((er) => er.type === ReviewerOfEmployeeType.External).map((er) => er.reviewer.id))
    .size
  const totalOfEmployees = activeEmployeesWithoutPartners.length
  const totalOfEmployeesWithMoreReviewers = Object.values(
    existingRelations.reduce((acc, current) => {
      acc[current.employee.id] = (acc[current.employee.id] || 0) + 1
      return acc
    }, {} as Record<string, number>),
  ).filter((v) => v > 1).length

  const onModalOpen = () => {
    setPossibleReviewers(activeEmployees)
    setOpen(true)
  }

  return (
    <>
      <ReviewerAddModal
        open={open}
        onCancel={() => setOpen(false)}
        activeEmployeesWithoutPartners={activeEmployeesWithoutPartners}
        companyName={selected.companyName}
        activeEmployees={possibleReviewers}
        companyCountry={selected}
        existingRelations={existingRelations}
      />

      <PerInfoBox>
        <InfoBoxTile
          heading={t('companySettingsView.reviewerTab.infoBox.reviewer.heading')}
          contentFlex="fixed"
          headingAlign="center"
          contentAlign="center"
        >
          <PerInfoBoxNumberElement number={totalOfReviewer} />
        </InfoBoxTile>

        <PerInfoBoxDivider />

        <InfoBoxTile heading={t('companySettingsView.reviewerTab.infoBox.types.heading')} headingAlign="left">
          <PerInfoBoxTagElement name={t('companySettingsView.reviewerTab.infoBox.types.internal')} color="cyan" number={totalOfInternal} />
          <PerInfoBoxTagElement name={t('companySettingsView.reviewerTab.infoBox.types.external')} color="gold" number={totalOfExternal} />
        </InfoBoxTile>

        <PerInfoBoxDivider />

        <InfoBoxTile heading={t('companySettingsView.reviewerTab.infoBox.employeesWithMoreReviewers.heading')} contentFlex="fixed">
          <PerInfoBoxNumberElement
            number={totalOfEmployeesWithMoreReviewers}
            outOf={totalOfEmployees + ' ' + t('companySettingsView.reviewerTab.infoBox.employeesWithMoreReviewers.employees')}
          />
        </InfoBoxTile>

        {canEdit && (
          <>
            <PerInfoBoxDivider />

            <InfoBoxTile
              heading={t('companySettingsView.reviewerTab.infoBox.add.heading')}
              headingAlign="center"
              contentFlex="fixed"
              contentAlign="center"
            >
              <PerInfoBoxActionButtonElement
                icon="add_stroke"
                color="success"
                tooltip={t('companySettingsView.reviewerTab.infoBox.add.btnTooltip')}
                onClick={() => onModalOpen()}
              />
            </InfoBoxTile>
          </>
        )}
      </PerInfoBox>
    </>
  )
}
